import {
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip as ChartTooltip,
} from "chart.js";
import { useMemo, useState } from "react";
import { Bar } from "../../../../../../theme/charts";
import { formatUSPhone } from "src/utils";
import { CompanyNumber } from "../..";
import { useUsageSummaryQuery } from "../hooks/useUsageSummaryQuery";

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTooltip, Legend);

const ORANGE = "#FB8E00";
const ORANGE_LIGHT = "#FFD4A3";
const BLUE = "#3B82F6";
const BLUE_LIGHT = "#93C5FD";

const refreshSpinKeyframes = {
  "@keyframes flowMobileUsageSpin": {
    from: { transform: "rotate(0deg)" },
    to: { transform: "rotate(360deg)" },
  },
};

const selectSx = {
  borderRadius: "10px",
  fontSize: 14,
  fontWeight: 600,
  bgcolor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.02)",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.1)",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: ORANGE,
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: ORANGE,
  },
};

function getCurrentBillingPeriod(): string {
  const now = new Date();
  const year = now.getUTCFullYear();
  const month = String(now.getUTCMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
}

function getYearBillingPeriodOptions(year = new Date().getUTCFullYear()): Array<{ value: string; label: string }> {
  return Array.from({ length: 12 }, (_, index) => {
    const month = String(index + 1).padStart(2, "0");
    const value = `${year}-${month}`;

    return {
      value,
      label: formatBillingPeriodLabel(value),
    };
  }).reverse();
}

function formatBillingPeriodLabel(period: string): string {
  const [year, month] = period.split("-");
  const date = new Date(Number(year), Number(month) - 1, 1);
  return date.toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
}

interface FlowMobileUsageTabProps {
  companyNumbers: CompanyNumber[];
}

function UsageEmptyState({ billingPeriod }: { billingPeriod: string }) {
  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: 4,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 420,
          textAlign: "center",
          border: "1.5px dashed rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.1)",
          borderRadius: "16px",
          py: 5,
          px: 3,
          bgcolor: "rgba(251, 142, 0, 0.04)",
        }}
      >
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: "16px",
            mx: "auto",
            mb: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(251, 142, 0, 0.12)",
            color: ORANGE,
          }}
        >
          <BarChartOutlinedIcon sx={{ fontSize: 26 }} />
        </Box>

        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 800,
            color: "var(--fx-surface-text, #0F172A)",
            mb: 0.75,
            letterSpacing: "-0.02em",
          }}
        >
          No usage for this period
        </Typography>

        <Typography
          sx={{
            fontSize: 13,
            color: "var(--fx-surface-textsecondary, #64748B)",
            fontWeight: 500,
            lineHeight: 1.6,
            maxWidth: 320,
            mx: "auto",
          }}
        >
          There are no calls or messages recorded for {formatBillingPeriodLabel(billingPeriod)} on this line yet.
        </Typography>
      </Box>
    </Box>
  );
}

function hasUsageData(data?: {
  calls: { inboundMinutes: number; outboundMinutes: number };
  sms: { inboundCount: number; outboundCount: number };
}): boolean {
  if (!data) {
    return false;
  }

  const callUnits = data.calls.inboundMinutes + data.calls.outboundMinutes;
  const smsUnits = data.sms.inboundCount + data.sms.outboundCount;

  return callUnits > 0 || smsUnits > 0;
}

function UsageBarChart({
  title,
  unitLabel,
  inboundValue,
  outboundValue,
  inboundColor,
  outboundColor,
}: {
  title: string;
  unitLabel: string;
  inboundValue: number;
  outboundValue: number;
  inboundColor: string;
  outboundColor: string;
}) {
  const maxValue = Math.max(inboundValue, outboundValue, 1);

  const data = useMemo(
    () => ({
      labels: ["Inbound", "Outbound"],
      datasets: [
        {
          label: unitLabel,
          data: [inboundValue, outboundValue],
          backgroundColor: [inboundColor, outboundColor],
          borderRadius: 8,
          borderSkipped: false,
          barThickness: 22,
        },
      ],
    }),
    [inboundValue, outboundValue, inboundColor, outboundColor, unitLabel],
  );

  const options = useMemo(
    () => ({
      indexAxis: "y" as const,
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx: { raw: unknown }) => ` ${ctx.raw} ${unitLabel}`,
          },
        },
      },
      scales: {
        x: {
          beginAtZero: true,
          suggestedMax: maxValue * 1.15,
          grid: { color: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.04)", drawBorder: false },
          ticks: {
            precision: 0,
            font: { size: 11, weight: 600 },
            color: "var(--fx-surface-texttertiary, #94A3B8)",
          },
        },
        y: {
          grid: { display: false },
          ticks: {
            font: { size: 12, weight: 700 },
            color: "var(--fx-surface-textsecondary, #64748B)",
          },
        },
      },
    }),
    [maxValue, unitLabel],
  );

  return (
    <Box
      sx={{
        border: "1px solid rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.08)",
        borderRadius: "14px",
        bgcolor: "var(--fx-surface-base, #FFFFFF)",
        p: 2,
        boxShadow: "0 2px 10px rgba(0,0,0,0.03)",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 1.5 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 800, color: "var(--fx-surface-text, #0F172A)" }}>{title}</Typography>
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: "var(--fx-surface-texttertiary, #94A3B8)" }}>
          Total: {inboundValue + outboundValue} {unitLabel}
        </Typography>
      </Box>

      <Box sx={{ height: 110 }}>
        <Bar data={data} options={options} />
      </Box>

      <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: "var(--fx-surface-textsecondary, #64748B)" }}>
          <Box component="span" sx={{ color: inboundColor, fontWeight: 800 }}>
            {inboundValue}
          </Box>{" "}
          inbound
        </Typography>
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: "var(--fx-surface-textsecondary, #64748B)" }}>
          <Box component="span" sx={{ color: outboundColor, fontWeight: 800 }}>
            {outboundValue}
          </Box>{" "}
          outbound
        </Typography>
      </Box>
    </Box>
  );
}

export function FlowMobileUsageTab({ companyNumbers }: FlowMobileUsageTabProps) {
  const billingPeriodOptions = useMemo(() => getYearBillingPeriodOptions(), []);

  const [selectedBillingPeriod, setSelectedBillingPeriod] = useState(getCurrentBillingPeriod);

  const activeNumbers = useMemo(
    () => companyNumbers.filter((number) => number.status === "ACTIVE" && number.phoneNumber),
    [companyNumbers],
  );

  const [selectedPhone, setSelectedPhone] = useState<string>("");

  const resolvedPhone = selectedPhone || activeNumbers[0]?.phoneNumber || "";

  const { data, isLoading, isError, isFetching, refetch } = useUsageSummaryQuery(
    resolvedPhone,
    selectedBillingPeriod,
  );

  const isInitialLoading = isLoading && !data;
  const showEmptyUsage = !isInitialLoading && !isError && data && !hasUsageData(data);
  const currentBillingPeriod = getCurrentBillingPeriod();
  const isViewingCurrentPeriod = selectedBillingPeriod === currentBillingPeriod;

  if (activeNumbers.length === 0) {
    return (
      <Box
        sx={{
          px: 3,
          py: 3,
          borderRadius: "12px",
          mx: 3,
          mt: 2,
          border: "1px solid rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.08)",
          bgcolor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.015)",
        }}
      >
        <Typography sx={{ fontSize: 14, fontWeight: 900, color: "var(--fx-surface-text, #0F172A)" }}>No active phone numbers</Typography>
        <Typography sx={{ fontSize: 13, fontWeight: 500, color: "var(--fx-surface-textsecondary, #64748B)", mt: 0.5 }}>
          Usage data will appear here once you have an active Flow Mobile line.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: "auto",
        px: 3,
        pt: 2,
        pb: 3,
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 900, color: "var(--fx-surface-text, #0F172A)" }}>Usage overview</Typography>

            <Tooltip title="Refresh usage">
              <span>
                <IconButton
                  size="small"
                  onClick={() => refetch()}
                  disabled={!resolvedPhone}
                  aria-label="Refresh usage"
                  sx={{
                    ...refreshSpinKeyframes,
                    color: ORANGE,
                    border: "1px solid rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.08)",
                    bgcolor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.02)",
                    animation: isFetching ? "flowMobileUsageSpin 0.75s linear infinite" : "none",
                    "&:hover": {
                      bgcolor: "rgba(251,142,0,0.08)",
                    },
                    "&.Mui-disabled": {
                      animation: "none",
                    },
                  }}
                >
                  <RefreshIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          <Typography sx={{ fontSize: 12, fontWeight: 500, color: "var(--fx-surface-textsecondary, #64748B)", mt: 0.3 }}>
            {formatBillingPeriodLabel(selectedBillingPeriod)} · units only, not billing charges
          </Typography>
        </Box>

        <Box sx={{ minWidth: 220, display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 700,
                color: "var(--fx-surface-textsecondary, #64748B)",
                mb: 0.75,
                letterSpacing: "0.01em",
              }}
            >
              My phone numbers
            </Typography>

            <FormControl size="small" fullWidth>
              <Select
                value={resolvedPhone}
                onChange={(event) => setSelectedPhone(event.target.value)}
                displayEmpty
                sx={selectSx}
              >
                {activeNumbers.map((number) => (
                  <MenuItem key={number.uid} value={number.phoneNumber}>
                    {number.name ? `${number.name} · ` : ""}
                    {formatUSPhone(number.phoneNumber)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 700,
                color: "var(--fx-surface-textsecondary, #64748B)",
                mb: 0.75,
                letterSpacing: "0.01em",
              }}
            >
              Billing period
            </Typography>

            <FormControl size="small" fullWidth>
              <Select
                value={selectedBillingPeriod}
                onChange={(event) => setSelectedBillingPeriod(event.target.value)}
                sx={selectSx}
              >
                {billingPeriodOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {!isViewingCurrentPeriod ? (
              <Typography
                component="button"
                type="button"
                onClick={() => setSelectedBillingPeriod(currentBillingPeriod)}
                sx={{
                  mt: 0.75,
                  p: 0,
                  border: 0,
                  bgcolor: "transparent",
                  cursor: "pointer",
                  fontSize: 12,
                  fontWeight: 700,
                  color: ORANGE,
                  textAlign: "left",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                Back to current period
              </Typography>
            ) : null}
          </Box>
        </Box>
      </Box>

      {isInitialLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 6 }}>
          <CircularProgress size={28} sx={{ color: ORANGE }} />
        </Box>
      ) : isError ? (
        <Box
          sx={{
            px: 2,
            py: 2,
            borderRadius: "12px",
            border: "1px solid rgba(239,68,68,0.25)",
            bgcolor: "rgba(239,68,68,0.05)",
          }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#b91c1c" }}>
            Could not load usage for this number.
          </Typography>
        </Box>
      ) : showEmptyUsage ? (
        <UsageEmptyState billingPeriod={selectedBillingPeriod} />
      ) : (
        <>
          <UsageBarChart
            title="Voice calls"
            unitLabel="min"
            inboundValue={data?.calls.inboundMinutes ?? 0}
            outboundValue={data?.calls.outboundMinutes ?? 0}
            inboundColor={ORANGE}
            outboundColor={ORANGE_LIGHT}
          />

          <UsageBarChart
            title="Messages"
            unitLabel="msgs"
            inboundValue={data?.sms.inboundCount ?? 0}
            outboundValue={data?.sms.outboundCount ?? 0}
            inboundColor={BLUE}
            outboundColor={BLUE_LIGHT}
          />
        </>
      )}
    </Box>
  );
}
